import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Paper, CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectAllTasks } from '../redux/slice/taskSlice';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

const AIChatAssistant = () => {
  const [messages, setMessages] = useState([]); 
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const allTasks = useSelector(selectAllTasks);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return; 
    if (!GEMINI_API_KEY) {
      toast.error("Gemini API Key not found. Please check your .env.local file.");
      return;
    }

    const userMessage = { sender: 'user', text: inputMessage };
    setMessages((prevMessages) => [...prevMessages, userMessage]); 
    setInputMessage(''); 
    setIsLoading(true);

    try {
      const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
      const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" }); 

     
      let systemPrompt = `You are a helpful and intelligent task management assistant named "TaskMind AI".
      Your goal is to answer user questions related to their tasks, provide insights, and offer general productivity advice.
      
      The user currently has the following tasks:
      `;

      if (allTasks && allTasks.length > 0) {
        allTasks.forEach(task => {
          systemPrompt += `- ID: ${task.id}, Title: "${task.title}", Description: "${task.description}", Completed: ${task.completed ? 'Yes' : 'No'}`;
          if (task.category) systemPrompt += `, Category: "${task.category}"`;
          if (task.sentimentScore) systemPrompt += `, Sentiment: "${task.sentimentScore}" (on a scale of -5 to 5)`;
          // Add any other AI analysis results (like entity extraction) here if they are part of the task object
          // if (task.extractedEntities) systemPrompt += `, Key Entities: ${task.extractedEntities.join(', ')}`;
          systemPrompt += `\n`;
        });
      } else {
        systemPrompt += `- No tasks currently defined for the user.\n`;
      }
      systemPrompt += `\n`;
      
      // To enable multi-turn conversation, you could include previous messages in the prompt
      // For simplicity, for now we'll focus on a single turn using the full system prompt.
      // If you want actual multi-turn, you'd feed the 'messages' array to Gemini's chat history.
      // For advanced scenarios with `brain.js`:
      // 1. Analyze inputMessage with brain.js for intent (e.g., 'GET_TASK_COUNT', 'SUMMARIZE_TASKS').
      // 2. Based on intent, fetch specific data (e.g., only completed tasks).
      // 3. Craft the prompt for Gemini using *that specific data*.
      
      systemPrompt += `Based on the user's current tasks and their question: "${inputMessage}", provide a concise and helpful answer.`;
      
      const result = await model.generateContent(systemPrompt); 
      const responseText = result.response.text();

      setMessages((prevMessages) => [...prevMessages, { sender: 'ai', text: responseText }]);
      toast.success("AI response received!");

    } catch (error) {
      console.error("Error getting AI response:", error);
      toast.error(`Failed to get AI response: ${error.message}`);
      setMessages((prevMessages) => [...prevMessages, { sender: 'ai', text: "Sorry, I couldn't process that. Please try again." }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mt: 3, width: '100%', maxWidth: 600, margin: '20px auto', display: 'flex', flexDirection: 'column', height: 'auto', minHeight: 400 }}>
      <Typography variant="h5" gutterBottom>TaskMind AI Chat</Typography>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1, border: '1px solid #eee', borderRadius: '4px', mb: 2 }}>
        {messages.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 5 }}>
            Ask TaskMind AI about your tasks or productivity!
          </Typography>
        ) : (
          messages.map((msg, index) => (
            <Box key={index} sx={{
              display: 'flex',
              justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              mb: 1
            }}>
              <Paper variant="outlined" sx={{
                p: 1,
                maxWidth: '70%',
                bgcolor: msg.sender === 'user' ? '#e3f2fd' : '#f1f8e9', 
                borderRadius: '10px',
                borderBottomLeftRadius: msg.sender === 'user' ? '10px' : '0', 
                borderBottomRightRadius: msg.sender === 'user' ? '0' : '10px',
              }}>
                <Typography variant="body2">{msg.text}</Typography>
              </Paper>
            </Box>
          ))
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ask a question..."
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isLoading) { 
              handleSendMessage();
            }
          }}
          sx={{ mr: 1 }}
          disabled={isLoading}
        />
        <Button
          variant="contained"
          onClick={handleSendMessage}
          endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          disabled={isLoading}
        >
          Send
        </Button>
      </Box>
    </Paper>
  );
};

export default AIChatAssistant;
